function resolveJsonPointer(root: any, refPath: string): any {
  if (!refPath.startsWith('#/')) return undefined
  const segments = refPath
    .slice(2)
    .split('/')
    .map(s => s.replace(/~1/g, '/').replace(/~0/g, '~'))
  return segments.reduce((cur: any, seg: string) => cur?.[seg], root)
}

/**
 * Recursively walks a JSON Schema and replaces every `$ref` node with the
 * inlined definition it points to (resolved from the root schema's
 * `$defs` / `definitions`).
 *
 * - Only local JSON-Pointer refs (`#/...`) are supported.
 * - Circular refs are broken by tracking visited ref paths; a circular ref
 *   is left as-is rather than causing infinite recursion.
 * - The original schema object is never mutated — new objects are returned.
 * - `$defs` / `definitions` are omitted from the output since all refs have
 *   been inlined.
 */
export function dereferenceSchema(
  node: any,
  root: any,
  _resolving = new Set<string>()
): any {
  if (!node || typeof node !== 'object') return node

  // Resolve $ref
  if (typeof node.$ref === 'string') {
    if (_resolving.has(node.$ref)) {
      // Circular — return as-is to avoid infinite recursion
      return node
    }
    const resolved = resolveJsonPointer(root, node.$ref)
    if (!resolved) return node
    const nextResolving = new Set<string>(Array.from(_resolving).concat(node.$ref))
    const resolvedNode = dereferenceSchema(resolved, root, nextResolving)
    // Merge sibling properties (e.g. title, type) from the referencing node
    // over the resolved content so local overrides are preserved.
    const { $ref: _ref, ...siblings } = node
    return Object.keys(siblings).length > 0 ? { ...resolvedNode, ...siblings } : resolvedNode
  }

  if (Array.isArray(node)) {
    return node.map(item => dereferenceSchema(item, root, _resolving))
  }

  const result: Record<string, any> = {}
  for (const [key, value] of Object.entries(node)) {
    // Drop definition stores — all refs have been inlined
    if (key === '$defs' || key === 'definitions') continue
    result[key] = dereferenceSchema(value, root, _resolving)
  }
  return result
}
